import { useState } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import {
  DigitCodeInput,
  ButtonBase,
  FailureModal,
  translateGqlErrorCodesOrReturnThemDirectly,
} from '../../../../common';
import { acceptCryptoWithdrawalOffer } from '../actions/acceptCryptoWithdrawalOffer.action';
import { TCryptoTransferTranslations } from '../../../utils/getCryptoTransferTranslations';
import { datadogErrorHelper } from '../../../../common/utils/datadogErrorHelper';
import { WithdrawalSuccessModal } from '../components/WithdrawalSuccessModal/WithdrawalSuccessModal';
import {
  useDrawerMetadata,
  useModalDrawerContext,
} from '../../../../common/components/ModalDrawerContext/ModalDrawerContext';

const containerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'small',
  height: '100%',
  pt: 'large',
});

const titleStyle = css({
  color: 'neutrals.text_primary',
  fontSize: 'headline.large_semi_bold',
  fontWeight: 'headline.large_semi_bold',
  letterSpacing: 'headline.large_semi_bold',
  lineHeight: 'headline.large_semi_bold',
});

const subtitleStyle = css({
  color: 'neutrals.text_secondary',
  fontSize: 'body.medium',
  fontWeight: 'body.medium',
  letterSpacing: 'body.medium',
  lineHeight: 'body.medium',
});

const inputWrapper = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
});

const errorStyle = css({
  color: 'negative.stroke_primary',
  fontSize: 'body.medium',
  fontWeight: 'body.medium',
  letterSpacing: 'body.medium',
  lineHeight: 'body.medium',
});

interface TwoFactorScreenProps {
  translations: TCryptoTransferTranslations;
  withdrawalId: string;
}

const TwoFactorScreen = ({
  withdrawalId,
  translations,
}: TwoFactorScreenProps) => {
  const { showModal } = useModalDrawerContext();

  const { closeThisDrawer } = useDrawerMetadata();

  const { t } = useClientTranslation();

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const [isButtonLoading, setIsButtonLoading] = useState(false);

  const [finalCode, setFinalCode] = useState('');

  const [hasError, setHasError] = useState(false);

  const onChangeInput = (isFilled: boolean, code: string) => {
    setIsButtonDisabled(!isFilled);

    if (isFilled) {
      setFinalCode(code);
    } else {
      setHasError(false);
    }
  };

  const onSubmitCode = () => {
    setIsButtonLoading(true);

    acceptCryptoWithdrawalOffer({
      code: finalCode,
      withdrawalId,
    })
      .then(async (response) => {
        setIsButtonLoading(false);

        // TODO: "bad.request.invalid.2fa.code" should be removed after releasing
        // all tenants to the consolidation in production.
        if (response.errors) {
          if (
            response.errors.includes('bad.request.invalid.2fa.code') ||
            response.errors.includes('mfa.invalid')
          ) {
            setHasError(true);

            return;
          }

          const { genericTitle, translatedCodes } =
            await translateGqlErrorCodesOrReturnThemDirectly(response.errors);

          closeThisDrawer();

          showModal(FailureModal, {
            title: genericTitle,
            description: translatedCodes.join(', '),
            tryAgainLink: {
              label: translations.goToPortfolio,
              url: '/portfolio',
            },
          });

          return;
        }

        const { acceptCryptoWithdrawal } = response.data;

        closeThisDrawer();

        showModal(WithdrawalSuccessModal, {
          withdrawalData: {
            asset: acceptCryptoWithdrawal?.asset,
            assetAmount: acceptCryptoWithdrawal?.assetAmount,
            fiatAmount: acceptCryptoWithdrawal?.fiatAmount,
          },
        });
      })
      .catch((error: Error) => {
        setIsButtonLoading(false);

        closeThisDrawer();

        showModal(FailureModal, {
          title: t('general_error_message_title'),
          description: `[MISSING TRANSLATION] We are actively monitoring these types of errors and will take steps to prevent similar occurrences in the future.`,
          tryAgainLink: {
            label: translations.goToPortfolio,
            url: '/portfolio',
          },
        });

        datadogErrorHelper({
          errorMessage: `[ERR][CRYPTO WITHDRAWAL 2FA SCREEN AFTER CONFIRM PRESSED] ${error.message}`,
          errorSeverity: DatadogErrorLevels.CRITICAL,
        });
      });
  };

  return (
    <div className={containerStyle}>
      <span className={titleStyle}>
        {translations.cryptoWithdrawal2FATitle}
      </span>

      <span className={subtitleStyle}>
        {translations.cryptoWithdrawal2FASubtitle}
      </span>

      <div className={inputWrapper}>
        <DigitCodeInput hasError={hasError} onChange={onChangeInput} />

        {hasError && (
          <span className={errorStyle}>
            {translations.cryptoWithdrawal2FAInvalidCode}
          </span>
        )}
      </div>

      <div
        className={css({
          marginTop: 'auto',
          display: 'flex',
          justifyContent: 'center',
          py: 'extra_small',
        })}
      >
        <ButtonBase
          isLoading={isButtonLoading}
          disabled={isButtonDisabled || isButtonLoading}
          onClick={onSubmitCode}
          size="large"
          fullWidth
        >
          {translations.continue}
        </ButtonBase>
      </div>
    </div>
  );
};

export { TwoFactorScreen };
